/**
 * The view toggles' geometry, in one place.
 *
 * The component draws from it, and the desktop frame needs the pills' widths to
 * keep the lockup and the strip's links clear of them. Every number is measured
 * off the Sept 19 references in a 375 x 812 frame — the artboards' 1125 x 2436
 * points at 3x.
 *
 * `TOGGLE_SCALE` is the one line to change if that frame turns out to be
 * 390 x 844, as the Sept 16 phone cards read it: 390 / 375 redraws the whole
 * control about 4% larger. The cards beside it are drawn at the larger reading,
 * so the toggle lands about 4% smaller than they do. That is the frame, not a
 * bug.
 *
 * One pill of this design since the Sept 23 afternoon chrome (contract §15):
 * Layout (List | Calendar | Map), drawn as icons on every device, the words
 * its labels. Scope became the Saved button. A pill is its items, so its width
 * is theirs.
 */

/** The frame the numbers below were measured in. */
export const TOGGLE_FRAME_W = 375;

/** Switch frames here. */
export const TOGGLE_SCALE = 1;

/**
 * The layout pill in the header's centred group, from 600px up: level with the
 * city pill and the Saved button rather than a head taller than them. A
 * phone's, at the foot of the screen, stays full size.
 */
export const TOGGLE_HEADER_SCALE = 0.75;

/** The layout icons (`ui:layout-*`, Sam's artwork in `_Dev process`). */
export type ToggleIcon = 'list' | 'calendar' | 'map';

export interface ToggleItem<K extends string = string> {
  key: K;
  /** Drawn where the pill has words; with icons, the icon's aria-label and tooltip. */
  label: string;
  icon?: ToggleIcon;
}

export type Scope = 'explore' | 'saved';
export type Layout = 'list' | 'calendar' | 'map';

export const LAYOUT_ITEMS: readonly ToggleItem<Layout>[] = [
  { key: 'list', label: 'List', icon: 'list' },
  { key: 'calendar', label: 'Calendar', icon: 'calendar' },
  { key: 'map', label: 'Map', icon: 'map' },
];

const RAW = {
  height: 52.6,
  /** The highlight's inset from the pill's top, bottom and ends. */
  inset: 9.8,
  /** 52.6 - 2 x 9.8. */
  highlight: 33,
  /**
   * A worded box is its bold label plus 2 x 15.5, measured in Canela Deck at
   * 15px (Explore 55.5 + 31 = 86.5, as the reference draws it). Labels not in
   * the table are estimated, so a new one still draws, a little off.
   */
  box: { explore: 86.5, schedule: 120.8, saved: 72.4, list: 57.9, calendar: 96.9, map: 62.4 } as Record<string, number>,
  /** An icon's box: the highlight a short pill around a 20px glyph rather than a circle. */
  iconBox: 54,
  icon: 20,
  label: { off: 14.1, on: 15 },
  /** Measured to the physical screen bottom, so a safe area replaces it rather than adding to it. */
  bottom: 18.4,
  /** What the band keeps clear above the pill. */
  bandGap: 12,
  /** Between the city pill, the layout pill and the Saved button, in the header's group. */
  groupGap: 14,
  /**
   * The city pill ("NYC", `CityPill`'s header size) and the widest Saved
   * button (a three-digit count), as the frame reserves them. The group is
   * anchored on the layout pill's centre, so neither growing moves anything.
   */
  cityPill: 64,
  savedMax: 144,
} as const;

/** A phone's pill sits on the page; a desktop's sits over it, and lets it through. */
export const TOGGLE_FILL = { phone: 'rgba(11,9,9,0.75)', desktop: 'rgba(11,9,9,0.40)' } as const;

export interface ToggleBox {
  key: string;
  /** From the pill's left edge. */
  x: number;
  w: number;
}

export interface ToggleMetrics {
  scale: number;
  width: number;
  height: number;
  inset: number;
  highlight: number;
  boxes: ToggleBox[];
  icon: number;
  labelOff: number;
  labelOn: number;
}

const boxOf = (item: ToggleItem, icons: boolean) =>
  icons && item.icon ? RAW.iconBox : (RAW.box[item.key] ?? item.label.length * 8 + 31);

export function toggleMetrics(items: readonly ToggleItem[], icons = false, scale = 1): ToggleMetrics {
  const s = TOGGLE_SCALE * scale;
  const boxes: ToggleBox[] = [];
  let x = RAW.inset;
  for (const item of items) {
    const w = boxOf(item, icons);
    boxes.push({ key: item.key, x: x * s, w: w * s });
    x += w;
  }
  return {
    scale: s,
    width: (x + RAW.inset) * s,
    height: RAW.height * s,
    inset: RAW.inset * s,
    highlight: RAW.highlight * s,
    boxes,
    icon: RAW.icon * s,
    labelOff: RAW.label.off * s,
    labelOn: RAW.label.on * s,
  };
}

/** A pill's width: what the frame keeps the lockup and links clear of. */
export const toggleWidth = (items: readonly ToggleItem[], icons = false, scale = 1) =>
  toggleMetrics(items, icons, scale).width;

/** The widest the Saved button gets, and the city pill, as the group reserves them. */
export const SAVED_BUTTON_MAX_W = RAW.savedMax;
export const CITY_PILL_W = RAW.cityPill;

/** Between the pieces of the header's group. */
export const GROUP_GAP = RAW.groupGap;

/**
 * The header's centred group as the desktop frame sees it (contract §15): how
 * far it reaches left and right of the point it is anchored on. That point is
 * the layout pill's centre, so the city pill sits left of it and the Saved
 * button right, and a count going from 9 to 10 moves nothing.
 */
export interface ToggleSpan {
  left: number;
  right: number;
}

export const groupSpan = (scale = TOGGLE_HEADER_SCALE): ToggleSpan => {
  const half = toggleWidth(LAYOUT_ITEMS, true, scale) / 2;
  return {
    left: half + GROUP_GAP + RAW.cityPill,
    right: half + GROUP_GAP + RAW.savedMax,
  };
};

/** The phone pill's gap to the physical bottom of the screen. */
export const TOGGLE_BOTTOM = RAW.bottom * TOGGLE_SCALE;

/** `bottom` for the phone's fixed pill: the safe area replaces the measured gap. */
export const TOGGLE_BOTTOM_CSS = `max(${TOGGLE_BOTTOM}px, env(safe-area-inset-bottom, 0px))`;

/**
 * The strip along the bottom of a phone that belongs to the toggle: the pill,
 * its gap to the screen and 12px above it. Anything that would otherwise sit
 * under the toggle clears this.
 */
export const TOGGLE_BAND = `calc(${RAW.height * TOGGLE_SCALE}px + ${TOGGLE_BOTTOM_CSS} + ${RAW.bandGap}px)`;

/**
 * The band in pixels, resolved against this browser's own safe area — for the
 * few places that have to measure rather than write a length in CSS.
 */
export function toggleBandPx(): number {
  const probe = document.createElement('div');
  probe.style.cssText = `position:fixed;left:-9999px;visibility:hidden;height:${TOGGLE_BAND}`;
  document.body.appendChild(probe);
  const height = probe.offsetHeight;
  probe.remove();
  return height;
}
